import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import faqdata from "./faq.pl.json";
import { useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "./firebaseConfig";
import { getShopBySlug } from "./services/shopService";
import useShopStore from "./store/useShopStore";

import Header from "./components/Header";
import BottomSheet from "./components/BottomSheet";
import HomePage from "./pages/HomePage";
import CheckoutPage from "./pages/CheckoutPage";
import ProfilePage from "./pages/ProfilePage";
import NotFoundPage from "./pages/NotFoundPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import LoginPage from "./pages/LoginPage";
import CompletePage from "./pages/CompletePage";

// --- Static / platform pages
import LandingPage from "./pages/LandingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import FaqPage from "./pages/FaqPage";
import ContactPage from "./pages/ContactPage";
import BecomePartnerPage from "./pages/BecomePartnerPage";

import "./styles/global.css";
import Footer from "./components/Footer";
import PlatformOrderTrackingPage from "./pages/PlatformOrderTrackingPage";

/** TEMP SWITCH:
 * Set to false to disable auth-based redirect to /profile for now.
 * We'll flip to true later when profile completion is required.
 */
const ENFORCE_PROFILE_COMPLETION = false;

/**
 * Get subdomain from hostname.
 * - Supports prod: alaturka.lungoapp.pl  -> "alaturka"
 * - Supports dev:  alaturka.localhost    -> "alaturka"
 * - Returns null for platform roots: localhost, lungoapp.pl, www.lungoapp.pl
 */
function getSubdomain(hostname) {
  // Dev convenience: alaturka.localhost
  if (hostname.endsWith(".localhost")) {
    const parts = hostname.split(".");
    // ["alaturka","localhost"] -> "alaturka"
    return parts.length >= 2 ? parts[0] : null;
  }

  // Plain localhost/127.0.0.1/::1 -> platform
  if (
    hostname === "localhost" ||
    hostname.startsWith("localhost:") ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  ) {
    return null;
  }

  const parts = hostname.split(".");
  // lungoapp.pl or www.lungoapp.pl -> platform
  if (parts.length <= 2) return null;
  if (parts.length === 3 && parts[0] === "www") return null;

  // alaturka.lungoapp.pl -> "alaturka"
  return parts[0] || null;
}

function AppRoutes() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        if (ENFORCE_PROFILE_COMPLETION) {
          const ref = doc(db, "users", firebaseUser.uid);
          const snap = await getDoc(ref);
          const data = snap.data() || {};
          if (
            !snap.exists() ||
            !data.username ||
            !data.phone ||
            !data.address
          ) {
            navigate("/profile");
          }
        }
      } else {
        setUser(null);
        // temp: no redirect for anonymous
      }
      setChecking(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  if (checking) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>Ładowanie...</div>
    );
  }

  return (
    <>
      <Header />
      <Routes>
        {/* SHOP FLOW */}
        <Route path="/" element={<HomePage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* ORDER TRACKING (support both /:orderId and /:id) */}
        {/* <Route
          path="/order-tracking/:orderId"
          element={<OrderTrackingPage />}
        /> */}
        {/* <Route path="/order-tracking/:id" element={<OrderTrackingPage />} /> */}

        <Route path="/order-complete" element={<CompletePage />} />
        <Route path="/login" element={<LoginPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <BottomSheet />
      <Footer />
    </>
  );
}

function PlatformRoutes() {
  const location = useLocation();
  const hideHeader = location.pathname === "/"; // hide only on landing page

  return (
    <>
      {!hideHeader && <Header />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/faq" element={<FaqPage data={faqdata} />} />
        <Route path="/contact-us" element={<ContactPage />} />
        <Route path="/become-a-partner" element={<BecomePartnerPage />} />
        <Route
          path="/order-tracking/:orderId"
          element={<PlatformOrderTrackingPage />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
    </>
  );
}

const ShopNotFound = () => (
  <div style={{ textAlign: "center", padding: 40 }}>
    Sklep nie został znaleziony. Sprawdź adres lub wróć na stronę główną.
  </div>
);

const App = () => {
  const [loading, setLoading] = useState(true);
  const [shopLookupDone, setShopLookupDone] = useState(false);
  const [shopMissingButSlugPresent, setShopMissingButSlugPresent] =
    useState(false);

  const setShop = useShopStore((state) => state.setShop);
  const shop = useShopStore((state) => state.shop);

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const hostname = window.location.hostname;
        const slug = getSubdomain(hostname);

        if (!slug) {
          // platform / static mode
          setShop(null);
          setShopLookupDone(true);
          return;
        }

        const shopData = await getShopBySlug(slug);
        if (!shopData) {
          setShop(null);
          setShopMissingButSlugPresent(true);
          return;
        }

        setShop(shopData);
      } catch (error) {
        console.error("Failed to fetch shop:", error);
      } finally {
        setLoading(false);
        setShopLookupDone(true);
      }
    };

    fetchShop();
  }, [setShop]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        Ładowanie sklepu...
      </div>
    );
  }

  return (
    <Router>
      <div className="app-container" style={{ display: "flex" }}>
        <div className="main-content" style={{ flex: 1 }}>
          {/* slug present but no shop doc */}
          {shopLookupDone && shopMissingButSlugPresent && <ShopNotFound />}

          {/* platform / static */}
          {shopLookupDone && !shop && !shopMissingButSlugPresent && (
            <PlatformRoutes />
          )}

          {/* shop flow */}
          {shop && <AppRoutes />}
        </div>
      </div>
    </Router>
  );
};

export default App;
